import clsx from 'clsx';
import TooltipWrapper from 'components/Tooltip';
import {useTooltip} from 'components/Tooltip/hook';
import {FC, HTMLAttributes, ReactNode} from 'react';
import {Controller, useFormContext} from 'react-hook-form';

import UIDrivenCheckbox from './checkbox';
import UIDrivenGranularRight from './granularRight';
import {useSection, useUIElements} from './uiContextProvider';

type SectionHeaderProps = {
    children?: ReactNode;
    name?: string;
    title: string;
};

const SectionHeader: FC<SectionHeaderProps> = ({children, name, title}) => {
    const text = name ? <label htmlFor={name}>{title}</label> : title;
    const header = (
        <h2 className={clsx('m-0', children ? 'ml-2' : 'ml-0')}>{text}</h2>
    );
    const hr = <hr className="m-0 mt-2" />;

    return children ? (
        <>
            <div className="ml-1 flex items-center">
                {children}
                {header}
            </div>
            {hr}
        </>
    ) : (
        <div>
            {header}
            {hr}
        </div>
    );
};

type SectionCheckboxProps = {
    disabled?: boolean;
    isChecked: boolean;
    name: string;
};

const SectionCheckbox: FC<SectionCheckboxProps> = ({
    disabled,
    isChecked,
    name,
}) => {
    const {setTooltipRef} = useTooltip();
    const {control} = useFormContext();

    return (
        <Controller
            control={control}
            defaultValue={isChecked}
            name={name}
            render={({field: {onBlur, onChange, ref, value: checked}}) => (
                <span ref={setTooltipRef}>
                    <input
                        checked={checked}
                        disabled={disabled}
                        id={name}
                        onBlur={onBlur}
                        onChange={onChange}
                        ref={ref}
                        type="checkbox"
                    />
                </span>
            )}
        />
    );
};

type SectionProps = {
    id: string;
} & HTMLAttributes<HTMLDivElement>;

const Section: FC<SectionProps> = ({id, ...props}) => {
    const section = useSection(id);
    const elements = useUIElements(id);
    if (!section) return null;
    if (!elements || elements.length === 0) return null;

    const {checkbox, title} = section;

    const headerContent =
        title && checkbox ? (
            <SectionHeader name={checkbox.name} title={title}>
                <SectionCheckbox
                    disabled={checkbox.disabled}
                    isChecked={checkbox.value}
                    name={checkbox.name}
                />
            </SectionHeader>
        ) : title ? (
            <SectionHeader title={title} />
        ) : null;

    const headerTooltip =
        checkbox?.disabledReason && checkbox.disabled ? (
            <TooltipWrapper message={checkbox.disabledReason}>
                {headerContent}
            </TooltipWrapper>
        ) : (
            headerContent
        );

    return (
        <div {...props} id={id}>
            {headerTooltip}
            {elements.map((element) => {
                if (element.type === 'checkbox') {
                    return <UIDrivenCheckbox key={element.name} {...element} />;
                }

                if (element.type === 'granular_right') {
                    return (
                        <UIDrivenGranularRight
                            key={element.name}
                            {...element}
                        />
                    );
                }
                throw new Error(
                    // In theory this should never happen but just in case hats sends something strange
                    // @ts-expect-error TODO: fix type
                    `Unexpected UI element type provided${element.type}`,
                );
            })}
        </div>
    );
};
export default Section;
